import { createRoot } from 'react-dom/client';

import { VarMappingPropsWithValidation } from '@/components/EvaluatorCrudPane/types';

import { VariableMappingDropdown } from '../VariableMappingDropdown';

// Cache React roots so that each widget instance only creates a root once.
// The key for the cache MUST be unique per widget instance (e.g. position in the document),
// otherwise the same DOM node would be moved around in the document tree.
const dropdownRootCache = new Map<
  string,
  {
    container: HTMLElement;
    root: ReturnType<typeof createRoot>;
  }
>();

// helper to unmount and clear all cached dropdown roots. This is useful when the
// containing editor component is unmounted to avoid memory leaks.
export function unmountVariableMappingDropdowns(
  evaluatorContext: VarMappingPropsWithValidation['storeType']
): void {
  dropdownRootCache.forEach(({ root }, key) => {
    if (key.startsWith(evaluatorContext)) {
      // for a race condition warning, we should unmount react root
      // on the next frame after unmount is called.
      queueMicrotask(() => {
        root.unmount();
      });
      dropdownRootCache.delete(key);
    }
  });
}

const handleRename = (
  oldKey: string,
  newKey: string,
  onRename: (oldKey: string, newKey: string) => [boolean, string],
  evaluatorContext: VarMappingPropsWithValidation['storeType'],
  position?: number
) => {
  const oldCacheKey =
    position !== undefined
      ? `${evaluatorContext}-${oldKey}-${position}`
      : `${evaluatorContext}-${oldKey}`;
  const newCacheKey =
    position !== undefined
      ? `${evaluatorContext}-${newKey}-${position}`
      : `${evaluatorContext}-${newKey}`;

  const entry = dropdownRootCache.get(oldCacheKey);
  if (entry) {
    dropdownRootCache.set(newCacheKey, {
      container: entry.container,
      root: entry.root,
    });
    dropdownRootCache.delete(oldCacheKey);
  }
  return onRename(oldKey, newKey);
};

const handleRemove = (
  id: string,
  onRemove: () => void,
  evaluatorContext: VarMappingPropsWithValidation['storeType'],
  position?: number
) => {
  const cacheKey =
    position !== undefined
      ? `${evaluatorContext}-${id}-${position}`
      : `${evaluatorContext}-${id}`;

  const entry = dropdownRootCache.get(cacheKey);
  if (entry) {
    entry.root.unmount();
    dropdownRootCache.delete(cacheKey);
  }
  onRemove();
};

export function createVariableMappingDropdown({
  evaluatorContext,
  variableMappingKey,
  variableMappingValue,
  availablePaths,
  onSelect,
  onRename,
  setActiveVariable,
  onRemove,
  uneditableVariables,
  showValidationErrors,
  readOnly,
  isSuggestions = false,
  position,
}: {
  evaluatorContext: VarMappingPropsWithValidation['storeType'];
  variableMappingKey: string;
  variableMappingValue: string;
  availablePaths: string[];
  onSelect: (key: string, path: string) => void;
  onRename: (oldKey: string, newKey: string) => [boolean, string];
  setActiveVariable: (activeVariable: string) => void;
  onRemove: () => void;
  uneditableVariables: string[];
  showValidationErrors: boolean;
  readOnly?: boolean;
  isSuggestions?: boolean;
  position?: number;
}) {
  // Create unique cache key that includes position to handle duplicate variables
  const cacheKey =
    position !== undefined
      ? `${evaluatorContext}-${variableMappingKey}-${position}`
      : `${evaluatorContext}-${variableMappingKey}`;

  let entry = dropdownRootCache.get(cacheKey);

  if (!entry) {
    // First time this widget is rendered – create container and root, then cache them.
    const container = document.createElement('span');
    const root = createRoot(container);
    entry = { container, root };
    dropdownRootCache.set(cacheKey, entry);
  }

  // Re-render (or initial render) with latest props. React will handle updates efficiently.
  entry.root.render(
    <VariableMappingDropdown
      key={cacheKey}
      variableMappingKey={variableMappingKey}
      variableMappingValue={variableMappingValue}
      availablePaths={availablePaths}
      onSelect={
        isSuggestions
          ? (key, path) => {
              // In suggestions mode, we want to rename the both the variable key and the selected path
              // This will update the actual prompt text instead of just the mapping value
              handleRename(key, path, onRename, evaluatorContext, position);
            }
          : onSelect
      }
      onRename={(oldKey, newKey) =>
        handleRename(oldKey, newKey, onRename, evaluatorContext, position)
      }
      setActiveVariable={setActiveVariable}
      onRemove={() =>
        handleRemove(variableMappingKey, onRemove, evaluatorContext, position)
      }
      uneditableVariables={uneditableVariables}
      showValidationErrors={showValidationErrors}
      readOnly={readOnly && evaluatorContext === 'detail'}
      isSuggestions={isSuggestions}
    />
  );

  return entry.container;
}
